import axios from "axios";
import { DataTable } from "simple-datatables";
import { handleRequestError, showAlert } from "../utils/alert";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  reportList: [],
  hasSearched: false,
  summary: {
    total_gross: 0,
    total_fees: 0,
    total_net: 0,
  },
  dataTableInstance: null,

  filters: {
    start_date: "",
    end_date: "",
  },

  fpStartDate: null,
  fpEndDate: null,

  async initRevenue() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // "Ping" na API para testar a permissão de acesso do usuário.
      try {
        await axios.get("/api/client/reports/revenue");
      } catch (pingError) {
        // Se a API barrar por falta de permissão/plano, lança o erro para o catch principal
        if (pingError.response && pingError.response.status === 403) {
          throw pingError;
        }
        // Ignora erros como 422 (campos de data obrigatórios ausentes),
        // pois isso indica que o middleware autorizou o acesso à rota.
      }

      // Usuário tem permissão, inicializa os componentes visuais
      this.initFlatpickr();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view imediatamente
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  initFlatpickr() {
    const config = {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      allowInput: true,
      altInput: true,
      altFormat: "d/m/Y",
    };

    this.fpStartDate = flatpickr(this.$refs.startDateInput, {
      ...config,
      onChange: (selectedDates, dateStr) => {
        this.filters.start_date = dateStr;
      },
    });

    this.fpEndDate = flatpickr(this.$refs.endDateInput, {
      ...config,
      onChange: (selectedDates, dateStr) => {
        this.filters.end_date = dateStr;
      },
    });
  },

  formatMoney(val) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  },

  formatDateBR(dateString) {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  },

  async loadReportTable() {
    if (!this.filters.start_date || !this.filters.end_date) {
      return showAlert(
        "Por favor, selecione as datas Inicial e Final para a apuração.",
        "warning",
      );
    }

    try {
      const queryParams = new URLSearchParams(this.filters).toString();
      const response = await axios.get(
        `/api/client/reports/revenue?${queryParams}`,
      );

      this.reportList = response.data.data;
      this.summary = response.data.summary;
      this.hasSearched = true;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#revenueReportTable tbody");
      if (!tbody) return;

      tbody.innerHTML = this.reportList
        .map((row) => {
          return `
          <tr class="hover:bg-slate-50 border-b border-gray-100">
            <td class="p-3 text-sm font-bold text-slate-800">${this.formatDateBR(row.date)}</td>
            <td class="p-3 text-sm font-mono text-slate-700">${this.formatMoney(row.gross_amount)}</td>
            <td class="p-3 text-sm font-mono text-orange-500">${row.fee_amount > 0 ? "-" + this.formatMoney(row.fee_amount) : "-"}</td>
            <td class="p-3 text-sm font-mono font-bold text-emerald-600">${this.formatMoney(row.net_amount)}</td>
            <td class="p-3 text-sm text-center text-slate-500"><span class="bg-slate-100 px-2 py-1 rounded-md text-xs font-bold">${row.order_count}</span></td>
          </tr>
        `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#revenueReportTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          noRows: "Nenhum faturamento registrado neste período.",
          info: "Mostrando {start} a {end} de {rows} dias",
        },
      });
    } catch (error) {
      // Bloqueia caso o plano vença ou mude durante o uso da tela
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }
      handleRequestError(error, "Erro ao processar faturamento.");
    }
  },

  applyFilters() {
    this.loadReportTable();
  },

  exportToCSV() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados disponíveis para exportação.", "warning");

    let csvContent = "\uFEFF";
    csvContent +=
      "Data;Faturamento Bruto;Taxas Retidas;Faturamento Líquido;Qtd OS\n";

    this.reportList.forEach((row) => {
      const formattedFee =
        row.fee_amount > 0 ? `-${row.fee_amount.toFixed(2)}` : "0.00";

      csvContent += `"${this.formatDateBR(row.date)}";"${row.gross_amount.toFixed(2)}";"${formattedFee}";"${row.net_amount.toFixed(2)}";"${row.order_count}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `relatorio_faturamento_${this.filters.start_date}_a_${this.filters.end_date}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  async exportToPDF() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados.", "warning");

    try {
      // 1. Pede um token descartável para o servidor de forma segura (via header)
      const response = await axios.post(
        "/api/client/reports/request-download-token",
      );
      const downloadToken = response.data.download_token;

      // 2. Prepara os filtros da URL
      const queryParams = new URLSearchParams(this.filters);

      // 3. Adiciona apenas o token descartável na URL
      queryParams.append("dtoken", downloadToken);

      // 4. Abre a nova aba
      window.open(
        `/client/reports/revenue/pdf?${queryParams.toString()}`,
        "_blank",
      );
    } catch (error) {
      handleRequestError(
        error,
        "Erro ao solicitar autorização para impressão.",
      );
    }
  },
});
